import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";

const GoogleDriveCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;

    const handleCallback = async () => {
      const code = searchParams.get("code");
      const error = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      if (error) {
        setStatus("error");
        setErrorMessage(`Authorization failed: ${errorDescription || error}`);
        toast.error(`Google Drive connection failed: ${error}`);
        return;
      }

      if (!code) {
        setStatus("error");
        setErrorMessage("No authorization code received");
        toast.error("No authorization code received from Google");
        return;
      }

      hasProcessed.current = true;

      try {
        const { data, error: functionError } = await supabase.functions.invoke(
          "cloud-storage",
          {
            body: {
              action: "oauth-callback",
              data: { code, provider: "google_drive" },
            },
          }
        );

        if (functionError || !data?.success) {
          throw new Error(data?.error || functionError?.message || "Failed to exchange authorization code");
        }

        setStatus("success");
        toast.success(`Google Drive connected as ${data.email}!`);

        setTimeout(() => {
          navigate("/genie-hub/knowledge-base?acquire=cloud-storage");
        }, 2000);
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : "Failed to complete Google Drive connection";
        setStatus("error");
        setErrorMessage(message);
        toast.error(`Failed to connect Google Drive: ${message}`);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {status === "loading" && <Loader2 className="h-5 w-5 animate-spin" />}
            {status === "success" && <CheckCircle className="h-5 w-5 text-green-600" />}
            {status === "error" && <XCircle className="h-5 w-5 text-red-600" />}
            {status === "loading" && "Connecting Google Drive..."}
            {status === "success" && "Google Drive Connected!"}
            {status === "error" && "Connection Failed"}
          </CardTitle>
          <CardDescription>
            {status === "loading" && "Please wait while we complete your Google Drive connection"}
            {status === "success" && "Your Google Drive account has been connected successfully"}
            {status === "error" && "There was a problem connecting your Google Drive account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === "loading" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <Loader2 className="h-12 w-12 animate-spin text-genie-primary" />
              <p className="text-sm text-muted-foreground text-center">
                Authenticating with Google and setting up your connection...
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
              <p className="text-sm text-muted-foreground text-center">Redirecting you to Cloud Storage...</p>
            </div>
          )}

          {status === "error" && (
            <div className="flex flex-col gap-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
              <Button onClick={() => navigate("/genie-hub/knowledge-base?acquire=cloud-storage")} className="w-full">
                Back to Cloud Storage
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleDriveCallback;
